import React, { useCallback, useEffect, useId, useRef, useState, lazy, Suspense } from 'react'
import AntigravityCanvas from './src/components/AntigravityCanvas'

import { BrowserCompatibility } from './src/components/BrowserCompatibility'
import LandingPage from './src/components/LandingPage'
import AboutPage from './src/components/AboutPage'
import AiAgencyPage from './src/components/AiAgencyPage'
import FixerPage from './src/components/FixerPage'
import WorkshopsPage from './src/components/WorkshopsPage'
import NotFoundPage from './src/components/NotFoundPage'
import PrivacyPolicy from './src/components/PrivacyPolicy'
import TermsOfService from './src/components/TermsOfService'
import { StageProvider } from 'src/context/StageContext'
import { ErrorBoundary } from './src/components/ui/ErrorBoundary'
import { SURFACE_ROLES } from './src/components/chat/design-tokens'
import AppHeader from './src/components/AppHeader'
import { useToast } from './src/context/ToastContext'
import { visualObservationCanonicalUserText, visualObservationSuccessText } from './src/lib/visual-observation-store.js'
import { appendCanonicalExchange } from './src/lib/append-canonical-exchange'
import { useTheme } from './src/context/ThemeContext'
import { useAppNavigation } from './src/hooks/useAppNavigation'
import { useVoiceChat } from './src/hooks/useVoiceChat'
import { useResearchRefs } from './src/hooks/useResearchRefs'
import { useResearchVisualAnimation } from './src/hooks/useResearchVisualAnimation'
import { useReportGeneration } from './src/hooks/useReportGeneration'
import { activityToolForVisitorChip, isVisitorWritingBrief } from './src/lib/funnel-agent-label'
import { clientSessionViewStore } from './services/clientSessionViewStore'
import { uiLocaleToLanguageCode } from './src/core/contracts'
import { resolveUiLocale } from './src/i18n/resolve-ui-locale'
import { LiveConnectionState, TranscriptItem, VisualShape } from 'types'
import type { FunnelStage } from 'src/core/types/funnel-stage'
import type { LiveAffectiveState, LiveSessionPolicy } from './src/types/live-policy'
import { DEFAULT_LIVE_AFFECTIVE_STATE, DEFAULT_LIVE_SESSION_POLICY } from './src/types/live-policy'
import { logger } from 'src/lib/logger-client'
import { shouldSuppressModelMessage } from 'src/lib/transcript-append'
import { resolveAgentShape } from 'src/logic/visualIntent'
import { shapeMorpher } from 'utils/visuals/morph'
import { calculateReasoningComplexity } from 'src/logic/visualComplexity'
import { useChatPanelState } from 'src/hooks/ui/useChatPanelState'
import { useActiveRoute } from 'src/hooks/ui/useActiveRoute'
import { useVisualState } from 'src/hooks/ui/useVisualState'
import { useSessionId } from 'src/hooks/ui/useSessionId'
import { useFreshSessionReset } from 'src/hooks/useFreshSessionReset'
import { useWebcamState } from 'src/hooks/ui/useWebcamState'
import { hasComposerBlockingStream, useThinkingState } from 'src/hooks/ui/useThinkingState'
import { useLocationState } from 'src/hooks/ui/useLocationState'
import { useServiceRegistry } from 'src/hooks/core/useServiceRegistry'
import { useChatSession } from 'src/hooks/core/useChatSession'
import { useLiveSessionBridge } from 'src/hooks/live/useLiveSessionBridge'
import { useLiveGateTurnHandler } from 'src/hooks/live/useLiveGateTurnHandler'
import { useCamera } from 'src/hooks/media/useCamera'
import { useScreenShare } from 'src/hooks/media/useScreenShare'
import { useMessageComposer } from 'src/hooks/app/useMessageComposer'
import {
  cleanupLiveSessionGenerationState,
  clearComposerGenerationState,
} from 'src/lib/composer-generation-cleanup'
import { reconcileLiveTranscriptUpdate } from 'src/lib/live-transcript-reconciler'
import { applyBookingWidgetToTranscript } from 'src/lib/transcript-side-effects'
import { generateTranscriptId } from 'src/lib/transcript-append'
import { useLeadResearch } from 'src/hooks/business/useLeadResearch'
import { useFutureConciergeFlow } from 'src/hooks/useFutureConciergeFlow'
import type { LiveRuntimeController } from 'src/live/liveRuntimeController'
import type { VoiceContextUpdate } from 'src/types/voice'

const AdminV2Dashboard = lazy(() =>
  import('./src/components/admin/AdminV2Dashboard').then(m => ({ default: m.AdminV2Dashboard }))
)
const FutureChatPage = lazy(() => import('./src/components/future-chat/FutureChatPage'))
import {
  mapTranscriptToFutureMessages,
  derivePresenceFromContext,
  buildFutureSessionFromTranscript,
} from './src/components/future-chat/future-chat-data-adapter'
import { FutureChatLoadingFallback } from './src/components/future-chat/FutureChatLoadingFallback'

function inferAffectiveStateFromText(
  text: string,
  current: LiveAffectiveState
): LiveAffectiveState {
  const normalized = text.toLowerCase()
  const next: LiveAffectiveState = {
    ...current,
    updatedAt: new Date().toISOString(),
    source: 'text',
  }

  if (/(frustrat|annoy|angry|wtf|broken|issue|problem|fucked|shit)/i.test(normalized)) {
    next.affect = 'frustrated'
    next.energy = 'high'
    next.preferredPace = 'slow'
    next.preferredVerbosity = 'balanced'
    next.confidence = 0.78
    return next
  }

  if (/(stress|overwhelm|tired|burnout|anxious)/i.test(normalized)) {
    next.affect = 'stressed'
    next.energy = 'low'
    next.preferredPace = 'slow'
    next.preferredVerbosity = 'terse'
    next.confidence = 0.72
    return next
  }

  if (/(amazing|awesome|love|great|perfect|brilliant|excited|holy shit)/i.test(normalized)) {
    next.affect = 'excited'
    next.energy = 'high'
    next.preferredPace = 'fast'
    next.preferredVerbosity = 'balanced'
    next.confidence = 0.7
    return next
  }

  if (/(thanks|thank you|nice|good)/i.test(normalized)) {
    next.affect = 'positive'
    next.energy = 'medium'
    next.preferredPace = 'normal'
    next.preferredVerbosity = 'balanced'
    next.confidence = 0.62
    return next
  }

  return next
}

import { inferLanguagePreference } from './src/lib/infer-language-preference.js'

export const App: React.FC = () => {
  const { showToast } = useToast()
  const { currentView, goToLanding, goToAdmin, setCurrentView } = useAppNavigation()
  const currentViewRef = useRef(currentView)
  const sessionId = useSessionId()
  useFreshSessionReset()
  const { aiBrainRef } = useServiceRegistry(sessionId)
  const { isDarkMode, toggleTheme } = useTheme()

  const liveServiceRef = useRef<LiveRuntimeController | null>(null)
  const mainContentId = useId()
  const transcriptRef = useRef<TranscriptItem[]>([])
  const latestWebcamFrameRef = useRef<string | null>(null)
  const latestScreenFrameRef = useRef<string | null>(null)
  const visualModalityStateRef = useRef({ webcam: false, screen: false })
  const abortControllerRef = useRef<AbortController | null>(null)
  const intentionalVoiceDisconnectRef = useRef(false)
  const connectionStateRef = useRef<LiveConnectionState>(LiveConnectionState.DISCONNECTED)
  const handleGenerateDiscoveryReportRef = useRef<() => void>(() => {})
  const [livePolicy, setLivePolicy] = useState<LiveSessionPolicy>(() => {
    const snapshot = clientSessionViewStore.getSnapshot() as {
      livePolicy?: LiveSessionPolicy
      language?: string
      uiLocale?: 'en' | 'nb'
      uiLocaleExplicit?: boolean
    }
    const languageCode = uiLocaleToLanguageCode(resolveUiLocale({
      uiLocale: snapshot.uiLocale,
      uiLocaleExplicit: snapshot.uiLocaleExplicit,
      language: snapshot.language,
      livePolicyLanguageCode: snapshot.livePolicy?.languageCode,
    }))
    return {
      ...DEFAULT_LIVE_SESSION_POLICY,
      ...(snapshot.livePolicy || {}),
      languageCode,
      affectiveState: {
        ...DEFAULT_LIVE_AFFECTIVE_STATE,
        ...(snapshot.livePolicy?.affectiveState || {}),
      },
    }
  })

  const { locationData } = useLocationState(liveServiceRef)
  const {
    isChatVisible,
    setIsChatVisible,
    activeTools,
    setActiveTools,
    currentReasoning,
    setCurrentReasoning,
    currentReasoningRef,
  } = useChatPanelState()
  const { activeRoute, setActiveRoute } = useActiveRoute()
  const { isWebcamActive, setIsWebcamActive, isWebcamActiveRef } = useWebcamState()
  const [currentAgentStage, setCurrentAgentStage] = useState<FunnelStage>('DISCOVERY')
  const [connectionState, setConnectionState] = useState<LiveConnectionState>(
    LiveConnectionState.DISCONNECTED
  )
  const {
    transcript,
    setTranscript,
    backendStatus,
    setBackendStatus,
    applyServerTurnResult,
    persistMessageToServer,
  } = useChatSession({ connectionState, sessionId })
  const canvasSurface = currentView === 'chat' || currentView === 'future-chat' ? 'chat' : 'landing'
  const {
    visualState,
    setVisualState,
    semanticShapeRef,
    handleVolumeChange,
    mapDataRef,
    weatherDataRef,
    chartDataRef,
    textContentRef,
  } = useVisualState({ transcript, activeRoute, isWebcamActiveRef, canvasSurface })
  const researchRefs = useResearchRefs()
  const { isThinking, setIsThinking, currentLoadingIdRef, toolsForMessageRef, toolIndexByIdRef } =
    useThinkingState(setTranscript, transcript)
  const lastSendRef = useRef<{ key: string; at: number } | null>(null)
  const inFlightSendKeysRef = useRef<Set<string>>(new Set())
  const lastTextInputRef = useRef<{ at: number } | null>(null)
  const restTurnOwnerRef = useRef<{ loadingId: string; startedAt: number } | null>(null)
  const handleTranscriptUpdateRef =
    useRef<
      (
        text: string,
        isUser: boolean,
        isFinal: boolean,
        groundingMetadata?: any,
        agentMetadata?: any,
        turnId?: string
      ) => void
    >()

  useEffect(() => {
    transcriptRef.current = transcript
  }, [transcript])

  useEffect(() => {
    clientSessionViewStore.setLivePolicy(livePolicy)
  }, [livePolicy])

  const handleToolCall = useCallback(
    async (functionCalls: any[]) => {
      const results = []
      let detectedAgent: string | null = null
      for (const call of functionCalls) {
        if (call.name !== 'update_dashboard') {
          if (call.name === 'switch_agent') detectedAgent = call.args.agentName
          continue
        }
        const { shape, text, data } = call.args
        if (!shape) continue
        const newShape = shape as VisualShape
        semanticShapeRef.current = newShape
        if (text) textContentRef.current = text
        if (data?.condition || data?.temperature) weatherDataRef.current = data
        if (data?.stockValue || data?.stockTrend) chartDataRef.current = data
        if (data?.locationTitle || data?.latitude) mapDataRef.current = data
        setVisualState(prev => ({
          ...prev,
          shape: newShape,
          ...(text && { textContent: text }),
          ...(data?.condition && { weatherData: data }),
          ...(data?.stockValue && { chartData: data }),
          ...(data?.locationTitle && { mapData: data }),
        }))
        results.push({
          id: call.id,
          name: call.name,
          result: { success: true, message: `Dashboard updated to ${shape}` },
        })
      }
      if (detectedAgent) {
        const agentShape = resolveAgentShape(detectedAgent, null)
        semanticShapeRef.current = agentShape
        setVisualState(prev => {
          if (prev.shape !== agentShape) {
            shapeMorpher.morph(prev.shape, agentShape)
          }
          return { ...prev, shape: agentShape }
        })
      }
      return results
    },
    [chartDataRef, mapDataRef, semanticShapeRef, setVisualState, textContentRef, weatherDataRef]
  )

  const handleTranscriptUpdate = useCallback(
    (
      text: string,
      isUser: boolean,
      isFinal: boolean,
      groundingMetadata?: any,
      agentMetadata?: any,
      turnId?: string
    ) => {
      logger.debug('[App] handleTranscriptUpdate', {
        text: text?.slice(0, 40),
        length: text?.length,
        isUser,
        isFinal,
        transcriptLength: transcriptRef.current?.length,
      })
      if (!isUser && shouldSuppressModelMessage('live-transcript')) {
        logger.debug('[App] Suppressed Live model transcript during REST turn (TurnRegistry)')
        return
      }
      if (!isUser && restTurnOwnerRef.current) {
        logger.debug('[App] Suppressed Live model transcript while REST turn in flight', {
          loadingId: restTurnOwnerRef.current.loadingId,
        })
        return
      }
      if (isFinal && (agentMetadata?.agent || agentMetadata?.stage)) {
        const agentShape = resolveAgentShape(agentMetadata.agent, agentMetadata.stage)
        semanticShapeRef.current = agentShape
        setVisualState(prev => {
          if (prev.shape !== agentShape) {
            shapeMorpher.morph(prev.shape, agentShape)
          }
          return { ...prev, shape: agentShape }
        })
        // Sync funnel stage to StageContext via prop
        if (agentMetadata?.stage) {
          setCurrentAgentStage(agentMetadata.stage as FunnelStage)
        }
      }
      const currentSourceCount = groundingMetadata?.groundingChunks?.length || 0
      const allSources = new Set<string>()
      transcriptRef.current.forEach(item => {
        item.groundingMetadata?.groundingChunks?.forEach(chunk => {
          const uri = chunk.web?.uri || chunk.maps?.uri
          if (uri) allSources.add(uri)
        })
      })
      const sourceCount = allSources.size > 0 ? allSources.size : currentSourceCount
      if (sourceCount > 0) setVisualState(prev => ({ ...prev, sourceCount }))
      const reasoning =
        agentMetadata?.reasoning ||
        transcriptRef.current[transcriptRef.current.length - 1]?.reasoning
      if (reasoning)
        setVisualState(prev => ({
          ...prev,
          reasoningComplexity: calculateReasoningComplexity(reasoning),
        }))

      const reconciliationState: {
        item: TranscriptItem | null
        changed: boolean
      } = { item: null, changed: false }
      setTranscript(prev => {
        const nextReconciliation = reconcileLiveTranscriptUpdate(prev, {
          text,
          isUser,
          isFinal,
          ...(turnId ? { turnId } : {}),
          ...(groundingMetadata !== undefined ? { groundingMetadata } : {}),
          ...(!isUser && currentLoadingIdRef.current
            ? { preferredEntryId: currentLoadingIdRef.current }
            : {}),
        })
        reconciliationState.item = nextReconciliation.item
        reconciliationState.changed = nextReconciliation.changed
        transcriptRef.current = nextReconciliation.transcript
        return nextReconciliation.transcript
      })

      const reconciledItem = reconciliationState.item
      if (
        reconciliationState.changed &&
        isFinal &&
        sessionId &&
        reconciledItem &&
        reconciledItem.text.trim()
      ) {
        void persistMessageToServer(
          sessionId,
          isUser ? 'user' : 'model',
          reconciledItem.text,
          new Date(),
          undefined,
          'voice_transcript'
        )
      }
    },
    [
      currentLoadingIdRef,
      persistMessageToServer,
      semanticShapeRef,
      setTranscript,
      setVisualState,
      sessionId,
      transcriptRef,
    ]
  )

  useEffect(() => {
    handleTranscriptUpdateRef.current = handleTranscriptUpdate
  }, [handleTranscriptUpdate])

  const gateReportTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const liveGateTurn = useLiveGateTurnHandler({
    sessionId,
    transcriptRef,
    currentLoadingIdRef,
    restTurnOwnerRef,
    reportTimeoutRef: gateReportTimeoutRef,
    clientSessionViewStore,
    setTranscript,
    setIsThinking,
    setBackendStatus,
    setCurrentReasoning,
    setActiveTools,
    applyServerTurnResult,
    setCurrentAgentStage,
    handleGenerateDiscoveryReport: summary => {
      ;(handleGenerateDiscoveryReportRef.current as (s?: unknown) => void)?.(summary)
    },
  })

  const handleOrchestratorGateStageUpdate = useCallback(
    (payload: {
      stage?: string
      agent?: string
      metadata?: Record<string, unknown>
      turnId?: string
    }) => {
      liveGateTurn.handleGateStageUpdate(payload)
    },
    [liveGateTurn]
  )

  const handleOrchestratorGateTurnComplete = useCallback(
    (payload: { turnId?: string; source?: string; text: string }) => {
      liveGateTurn.finalizeGateTurn(payload)
    },
    [liveGateTurn]
  )

  const handleLiveSessionEnd = useCallback(() => {
    liveGateTurn.abortGateTurn()
    setVisualState(prev => ({
      ...prev,
      isActive: false,
      audioLevel: 0,
      mode: 'idle',
      waveformEnabled: false,
    }))
    cleanupLiveSessionGenerationState({
      setTranscript,
      setIsThinking,
      currentLoadingIdRef,
      restTurnOwnerRef,
      abortControllerRef,
    })
  }, [liveGateTurn, setTranscript, setIsThinking, setVisualState])

  const {
    connectionState: liveConnectionState,
    isAudioInputActive,
    isAudioInputStarting,
    handleConnect,
    handleSendRealtimeInput,
    handleSendVideoFrame,
    handleSendContextUpdate,
    getAudioData,
  } = useLiveSessionBridge({
    sessionId,
    clientSessionViewStore,
    livePolicy,
    isWebcamActive,
    visualModalityStateRef,
    setActiveRoute,
    setIsChatVisible,
    setVisualState,
    setActiveTools,
    handleVolumeChange,
    handleTranscript: (text, isUser, isFinal, grounding, agent, turnId) =>
      handleTranscriptUpdateRef.current?.(text, isUser, isFinal, grounding, agent, turnId),
    handleToolCall,
    onSignal: payload => {
      if (payload.action === 'trigger_report') {
        handleGenerateDiscoveryReportRef.current()
        setVisualState(prev => ({ ...prev, shape: 'discovery', mode: 'idle' }))
      }
      if (payload.action === 'trigger_booking') {
        applyBookingWidgetToTranscript(setTranscript, payload.turnId)
      }
    },
    onUserDiscoveryReportIntent: () => {
      handleGenerateDiscoveryReportRef.current()
    },
    onOrchestratorGateStageUpdate: handleOrchestratorGateStageUpdate,
    onOrchestratorGateTurnComplete: handleOrchestratorGateTurnComplete,
    isGateTurnActive: () => Boolean(currentLoadingIdRef.current),
    liveServiceRef,
    onLiveSessionEnd: handleLiveSessionEnd,
  })

  useEffect(() => {
    setConnectionState(liveConnectionState)
  }, [liveConnectionState])

  useEffect(() => {
    currentViewRef.current = currentView
  }, [currentView])

  useEffect(() => {
    connectionStateRef.current = connectionState
  }, [connectionState])

  const leadResearch = useLeadResearch({
    sessionId,
    setTranscript,
    setIsWebcamActive,
    showToast,
    setVisualState,
    onVoiceConnect: handleConnect,
    canAutoStartVoice: () => {
      if (currentViewRef.current !== 'chat') return false
      return transcriptRef.current.some(
        item =>
          item.role === 'model' &&
          item.isFinal !== false &&
          Boolean(item.text?.trim()) &&
          item.text !== '...' &&
          item.attachment?.type !== 'intelligence_briefing'
      )
    },
    setView: setCurrentView,
    setChatVisible: setIsChatVisible,
    researchResultRef: researchRefs.researchResultRef,
    intelligenceContextRef: researchRefs.intelligenceContextRef,
  })

  useResearchVisualAnimation({
    isResearching: leadResearch.isResearching,
    setVisualState,
  })

  useEffect(() => {
    researchRefs.userProfileRef.current = leadResearch.userProfile
  }, [leadResearch.userProfile, researchRefs.userProfileRef])

  const futureConcierge = useFutureConciergeFlow({
    sessionId,
    userProfile: leadResearch.userProfile,
    isResearching: leadResearch.isResearching,
    preflightFailed: leadResearch.preflightFailed,
    preflightTimedOut: leadResearch.preflightTimedOut,
    researchStatus: leadResearch.researchStatus || '',
    transcript,
    handleTermsComplete: leadResearch.handleTermsComplete,
  })

  const handleWebcamContextUpdate = useCallback(
    (update: {
      sessionId?: string | null | undefined
      modality: 'screen' | 'webcam'
      analysis: string
      imageData?: string | undefined
      capturedAt?: number | undefined
      metadata?: Record<string, unknown> | undefined
    }) => {
      const normalized: VoiceContextUpdate = {
        modality: update.modality,
        analysis: update.analysis,
        ...(update.sessionId !== undefined ? { sessionId: update.sessionId } : {}),
        ...(update.imageData !== undefined ? { imageData: update.imageData } : {}),
        ...(update.capturedAt !== undefined ? { capturedAt: update.capturedAt } : {}),
        ...(update.metadata !== undefined ? { metadata: update.metadata } : {}),
      }
      handleSendContextUpdate(normalized)
    },
    [handleSendContextUpdate]
  )
  const getLiveTransportState = useCallback(() => {
    return (
      liveServiceRef.current?.getRealtimeTransportState() ?? {
        isConnected: false,
        isSessionReady: false,
        bufferedAmount: 0,
      }
    )
  }, [])
  const visualObservationNoteRef = useRef<Record<string, string>>({})
  const appendVisualObservation = useCallback(
    (modality: 'webcam' | 'screen', status: 'success' | 'error', detail: string) => {
      const normalizedDetail = detail.trim()
      if (!normalizedDetail) return
      const key = modality + ':' + status + ':' + normalizedDetail
      if (visualObservationNoteRef.current[modality] === key) return
      visualObservationNoteRef.current[modality] = key
      const attachmentData = modality + ':' + status
      const text =
        status === 'success'
          ? visualObservationSuccessText(modality, normalizedDetail)
          : normalizedDetail
      setTranscript(prev => {
        const existingIndex = prev.findIndex(
          item =>
            item.attachment?.type === 'visual_observation' &&
            item.attachment.data === attachmentData
        )
        const existing = existingIndex >= 0 ? prev[existingIndex] : undefined
        const nextItem: TranscriptItem = {
          ...(existing || {}),
          id: existing?.id || generateTranscriptId('visual-observation'),
          role: 'model',
          text,
          timestamp: new Date(),
          isFinal: true,
          status: 'complete',
          isLiveTranscript: false,
          attachment: {
            type: 'visual_observation',
            name: modality === 'screen' ? 'Screen observation' : 'Camera observation',
            data: attachmentData,
          },
        }
        if (existing && existing.text === text) return prev
        if (existingIndex >= 0) {
          const next = [...prev]
          next[existingIndex] = nextItem
          return next
        }
        return [...prev, nextItem]
      })
      const canonicalUserText = visualObservationCanonicalUserText(status, text)
      if (sessionId && canonicalUserText) {
        void appendCanonicalExchange(sessionId, canonicalUserText, '').catch(error => {
          logger.warn('[App] Media notice was not stored', {
            error: error instanceof Error ? error.message : String(error),
          })
        })
      }
    },
    [sessionId, setTranscript]
  )

  // Stable callbacks for useCamera — inline arrows caused captureFrame to recreate on every
  // App render, which cascaded into the auto-capture useEffect re-running and spamming logs.
  const handleWebcamAnalyzeVisualDelta = useCallback(
    (currentAnalysis: string, previousAnalysis?: string) => {
      if (!previousAnalysis) return { significant: true, score: 1 }
      const same = currentAnalysis.trim() === previousAnalysis.trim()
      return { significant: !same, score: same ? 0 : 0.8 }
    },
    []
  )
  const handleWebcamCapture = useCallback((_blob: Blob, imageData?: string) => {
    latestWebcamFrameRef.current = imageData ? imageData.split(',')[1] || imageData : null
  }, [])
  const handleWebcamAnalysis = useCallback(
    (analysis: string) => {
      logger.debug('[App] Webcam analysis:', { analysis })
      appendVisualObservation('webcam', 'success', analysis)
    },
    [appendVisualObservation]
  )
  const webcam = useCamera({
    sessionId,
    enableAutoCapture: true,
    requireVoiceSession: false,
    ...(liveServiceRef.current?.getConnectionId && liveServiceRef.current.getConnectionId()
      ? { voiceConnectionId: liveServiceRef.current.getConnectionId()! }
      : {}),
    sendRealtimeInput: handleSendRealtimeInput,
    sendVideoFrame: handleSendVideoFrame,
    sendContextUpdate: handleWebcamContextUpdate,
    analyzeVisualDelta: handleWebcamAnalyzeVisualDelta,
    getLiveTransportState,
    onCapture: handleWebcamCapture,
    onAnalysis: handleWebcamAnalysis,
  })

  useEffect(() => {
    if (webcam.error) appendVisualObservation('webcam', 'error', webcam.error)
  }, [appendVisualObservation, webcam.error])

  useEffect(() => {
    visualModalityStateRef.current.webcam = isWebcamActive
    if (!isWebcamActive && webcam.isActive) {
      webcam.stopCamera()
      latestWebcamFrameRef.current = null
      return
    }

    if (isWebcamActive && !webcam.isActive && !webcam.isInitializing) {
      void webcam.startCamera(undefined)
    }
    // Auto-connect Live for webcam streaming when no active session (audio off — Live owns text turns)
    if (isWebcamActive && connectionState !== 'CONNECTED' && connectionState !== 'CONNECTING') {
      void handleConnect({ audio: false })
    }
  }, [
    isWebcamActive,
    webcam.isActive,
    webcam.isInitializing,
    webcam.startCamera,
    webcam.stopCamera,
    connectionState,
    handleConnect,
  ])

  useEffect(() => {
    if (!webcam.isActive && !webcam.isInitializing) {
      latestWebcamFrameRef.current = null
    }
  }, [webcam.isActive, webcam.isInitializing])

  // #123 Phase C: Clear composer thinking/streaming state when voice mode ends.
  // Prevents "Still finishing..." block after voice disconnect.
  const prevBackendModeRef = useRef(backendStatus?.mode)
  useEffect(() => {
    const prev = prevBackendModeRef.current
    const curr = backendStatus?.mode
    prevBackendModeRef.current = curr

    // Live-first: voice → agents is intentional when a gate turn starts; do not clear loadingId.
    if (prev === 'voice' && curr !== 'voice' && curr !== 'agents') {
      cleanupLiveSessionGenerationState({
        setTranscript,
        setIsThinking,
        currentLoadingIdRef,
        restTurnOwnerRef,
        abortControllerRef,
      })
    }
  }, [backendStatus?.mode, setTranscript, setIsThinking, currentLoadingIdRef])

  const handleScreenShareAnalysis = useCallback(
    (analysis: string) => {
      logger.debug('[App] Screen share analysis:', { analysis })
      appendVisualObservation('screen', 'success', analysis)
    },
    [appendVisualObservation]
  )
  const handleScreenShareCapture = useCallback((_blob: Blob, imageData?: string) => {
    latestScreenFrameRef.current = imageData ? imageData.split(',')[1] || imageData : null
  }, [])
  const handlePauseHeartbeat = useCallback(() => {
    liveServiceRef.current?.pauseHeartbeat()
  }, [])
  const handleResumeHeartbeat = useCallback(() => {
    liveServiceRef.current?.resumeHeartbeat()
  }, [])
  const screenShare = useScreenShare({
    sessionId,
    enableAutoCapture: true,
    captureInterval: 4000,
    ...(liveServiceRef.current?.getConnectionId && liveServiceRef.current.getConnectionId()
      ? { voiceConnectionId: liveServiceRef.current.getConnectionId()! }
      : {}),
    sendRealtimeInput: handleSendRealtimeInput,
    sendVideoFrame: handleSendVideoFrame,
    sendContextUpdate: handleSendContextUpdate,
    analyzeVisualDelta: handleWebcamAnalyzeVisualDelta, // Same pure function — reuse
    onCapture: handleScreenShareCapture,
    onAnalysis: handleScreenShareAnalysis,
    onBeforeMediaDialog: handlePauseHeartbeat,
    onAfterMediaDialog: handleResumeHeartbeat,
    connectionState,
  })

  const messageComposer = useMessageComposer({
    sessionId,
    connectionState,
    aiBrainRef,
    liveServiceRef,
    transcriptRef,
    latestWebcamFrameRef,
    latestScreenFrameRef,
    restTurnOwnerRef,
    userProfileRef: researchRefs.userProfileRef,
    abortControllerRef,
    currentReasoning,
    currentReasoningRef,
    currentLoadingIdRef,
    toolsForMessageRef,
    toolIndexByIdRef,
    inFlightSendKeysRef,
    lastSendRef,
    lastTextInputRef,
    semanticShapeRef,
    textContentRef,
    weatherDataRef,
    chartDataRef,
    mapDataRef,
    isWebcamActive,
    isScreenShareActive: screenShare.isActive,
    performResearch: leadResearch.performResearch,
    persistMessageToServer,
    setTranscript,
    setBackendStatus,
    applyServerTurnResult,
    setVisualState,
    setActiveTools,
    setCurrentReasoning,
    setIsThinking,
    setLivePolicy,
    inferAffectiveStateFromText,
    inferLanguagePreference,
    showToast,
    handleGenerateDiscoveryReport: () => handleGenerateDiscoveryReportRef.current(),
    handleConnect,
    beginGateTurn: liveGateTurn.beginGateTurn,
    onConciergeGateBlocked: futureConcierge.resetConciergeGate,
  })

  // Auto-connect Live for screen share only from a clean disconnect.
  // ERROR is a Gemini 1007. Opening another session here re-seeds clientContent
  // the same second the screen UI says "reconnecting" and closes Live before booking.
  useEffect(() => {
    if (screenShare.isActive && connectionState === 'DISCONNECTED') {
      void handleConnect({ audio: false })
    }
  }, [screenShare.isActive, connectionState, handleConnect])

  useEffect(() => {
    visualModalityStateRef.current.screen = screenShare.isActive
    if (!screenShare.isActive && !screenShare.isInitializing) {
      latestScreenFrameRef.current = null
    }
  }, [screenShare.isActive, screenShare.isInitializing])

  useEffect(() => {
    if (!screenShare.error) return
    logger.warn('[App] Screen share failed', { error: screenShare.error })
    appendVisualObservation('screen', 'error', screenShare.error)
  }, [appendVisualObservation, screenShare.error])

  const prevVisualModalityRef = useRef({ webcam: false, screen: false })
  useEffect(() => {
    const prev = prevVisualModalityRef.current
    const webcamEnded = prev.webcam && !isWebcamActive
    const screenEnded = prev.screen && !screenShare.isActive
    if (webcamEnded || screenEnded) {
      cleanupLiveSessionGenerationState({
        setTranscript,
        setIsThinking,
        currentLoadingIdRef,
        restTurnOwnerRef,
        abortControllerRef,
      })
      // Stopping webcam/screenshare must not tear down the Fly Live socket.
    }
    prevVisualModalityRef.current = { webcam: isWebcamActive, screen: screenShare.isActive }
  }, [isWebcamActive, screenShare.isActive, setIsThinking, setTranscript])

  const handleCanvasShapeChange = useCallback(
    (shape: VisualShape) => {
      semanticShapeRef.current = shape
      setVisualState(prev => ({ ...prev, shape }))
    },
    [setVisualState]
  )

  const reportGeneration = useReportGeneration({
    sessionId,
    transcript,
    userProfile: leadResearch.userProfile,
    researchResultRef: researchRefs.researchResultRef,
    intelligenceContextRef: researchRefs.intelligenceContextRef,
    screenShareActive: screenShare.isActive,
    setTranscript,
    showToast,
    handleSendMessage: messageComposer.handleSendMessage,
  })

  useEffect(() => {
    handleGenerateDiscoveryReportRef.current = reportGeneration.handleGenerateDiscoveryReport
  }, [reportGeneration.handleGenerateDiscoveryReport])

  const wrappedStopVoice = useCallback(async () => {
    liveGateTurn.abortGateTurn()
    cleanupLiveSessionGenerationState({
      setTranscript,
      setIsThinking,
      currentLoadingIdRef,
      restTurnOwnerRef,
      abortControllerRef,
    })
    liveServiceRef.current?.setOutputMuted?.(true)
    // Always stop mic only — never disconnect() the Fly gateway on Voice OFF.
    await liveServiceRef.current?.stopVoiceInput?.()
  }, [liveGateTurn, setIsThinking, setTranscript])

  const voiceChat = useVoiceChat({
    connectionState,
    isAudioInputActive,
    isAudioInputStarting,
    transcript,
    backendStatus,
    isChatVisible,
    setIsChatVisible,
    isWebcamActive,
    setIsWebcamActive,
    handleConnect,
    handleDisconnect: wrappedStopVoice,
  })

  const handleStopGeneration = useCallback(() => {
    abortControllerRef.current?.abort()
    reportGeneration.abortReportPrepare()
    abortControllerRef.current = null
    clearComposerGenerationState({
      loadingId: currentLoadingIdRef.current,
      setTranscript,
      setIsThinking,
      currentLoadingIdRef,
      restTurnOwnerRef,
      mode: 'finalize',
    })
    liveServiceRef.current?.stopActiveGeneration?.()
  }, [reportGeneration.abortReportPrepare, setTranscript, setIsThinking])

  const handleSendBlocked = useCallback(
    (reason: string) => {
      showToast(reason, 'info')
    },
    [showToast]
  )

  const prevVoiceEnabled = useRef(voiceChat.voiceEnabled)
  useEffect(() => {
    if (prevVoiceEnabled.current && !voiceChat.voiceEnabled) {
      const visualStillActive = isWebcamActive || screenShare.isActive
      setVisualState(prev => ({
        ...prev,
        isActive: visualStillActive,
        audioLevel: 0,
        mode: 'idle',
        waveformEnabled: false,
      }))
      liveGateTurn.abortGateTurn()
      cleanupLiveSessionGenerationState({
        setTranscript,
        setIsThinking,
        currentLoadingIdRef,
        restTurnOwnerRef,
        abortControllerRef,
      })
      // Only kill webcam/screenshare when the USER intentionally disconnected voice.
      // If voice dropped due to media renegotiation (e.g. camera restart), leave media intact.
      if (intentionalVoiceDisconnectRef.current) {
        if (isWebcamActive) setIsWebcamActive(false)
        if (screenShare.isActive) void screenShare.stopScreenShare()
      }
      intentionalVoiceDisconnectRef.current = false
    }
    prevVoiceEnabled.current = voiceChat.voiceEnabled
  }, [
    voiceChat.voiceEnabled,
    liveGateTurn,
    isWebcamActive,
    setIsWebcamActive,
    screenShare.isActive,
    screenShare.stopScreenShare,
    setTranscript,
    setIsThinking,
    setVisualState,
    currentLoadingIdRef,
    restTurnOwnerRef,
  ])

  const renderFutureChatShell = () => {
    const hasStreamingTurn = hasComposerBlockingStream(transcript)
    const activeToolName = activityToolForVisitorChip({
      tools: activeTools,
      prepareTerminal: reportGeneration.prepareTerminal,
    })
    const latestLatency = [...transcript]
      .reverse()
      .find(item => item.role === 'model' && item.processingTime)?.processingTime
    const futureMessages = mapTranscriptToFutureMessages(transcript)
    const futurePresence = derivePresenceFromContext(clientSessionViewStore.getSnapshot(), {
      voiceActive: voiceChat.voiceEnabled,
      visionActive: webcam.isActive && webcam.hasVerifiedFrame,
      screenShared: screenShare.isActive,
      locationShared: !!locationData,
      memoryActive: leadResearch.isResearching,
    })
    const futureSession = buildFutureSessionFromTranscript(transcript, {
      currentAgentStage,
      presence: futurePresence,
    })
    const reportStatus =
      clientSessionViewStore.getServerProjectionSnapshot()?.projection.report.status
    const isWritingBrief = isVisitorWritingBrief({
      isPreparingReport: reportGeneration.isPreparingReport,
      reportStatus,
      prepareTerminal: reportGeneration.prepareTerminal,
    })

    return (
      <ErrorBoundary>
        <BrowserCompatibility />
        <Suspense fallback={<FutureChatLoadingFallback />}>
          <StageProvider>
            <FutureChatPage
              sessionId={sessionId}
              appVisualState={visualState}
              getAudioData={getAudioData}
              currentAgentStage={currentAgentStage}
              messages={futureMessages}
              session={futureSession}
              presence={futurePresence}
              agentMode={
                isAudioInputActive ? 'listening' : isThinking ? 'thinking' : visualState.mode
              }
              isConnected={
                connectionState !== LiveConnectionState.ERROR && backendStatus?.severity !== 'error'
              }
              {...(latestLatency ? { latency: latestLatency } : {})}
              isResearching={leadResearch.isResearching}
              isWritingBrief={isWritingBrief}
              researchDetail={leadResearch.researchStatus || undefined}
              preflightFailed={leadResearch.preflightFailed}
              preflightTimedOut={leadResearch.preflightTimedOut}
              isThinking={isThinking}
              isGenerating={isThinking || hasStreamingTurn}
              isStreaming={hasStreamingTurn}
              activeTool={activeToolName}
              isListening={isAudioInputActive}
              isVoiceConnected={voiceChat.voiceEnabled}
              isVoiceConnecting={isAudioInputStarting}
              isLiveSessionConnected={connectionState === LiveConnectionState.CONNECTED}
              isLiveConnecting={connectionState === LiveConnectionState.CONNECTING}
              onConnectVoice={voiceChat.connectVoice}
              onDisconnectVoice={voiceChat.disconnectVoice}
              isWebcamActive={isWebcamActive || webcam.isActive}
              webcamStream={webcam.stream}
              webcamError={webcam.error}
              onWebcamToggle={() => setIsWebcamActive(!isWebcamActive)}
              onWebcamClose={() => {
                webcam.stopCamera()
                setIsWebcamActive(false)
              }}
              onSwitchCamera={() => void webcam.switchCamera()}
              facingMode={webcam.facingMode}
              onWebcamVideoElement={webcam.attachVideoElement}
              isScreenShareActive={screenShare.isActive}
              screenStream={screenShare.stream}
              screenError={screenShare.error}
              onScreenShareToggle={() => void screenShare.toggleScreenShare()}
              onScreenShareClose={() => void screenShare.stopScreenShare()}
              onSendMessage={messageComposer.handleSendMessage}
              onStopGeneration={handleStopGeneration}
              onSendBlocked={handleSendBlocked}
              onToggleTheme={toggleTheme}
              onRequestLocation={() => void clientSessionViewStore.acquireBrowserLocation()}
              onGenerateReport={reportGeneration.handleGenerateDiscoveryReport}
              onDownloadTranscript={reportGeneration.handleGeneratePDF}
              onSendEmail={reportGeneration.handleEmailPDF}
              onGenerateMemo={reportGeneration.handleGenerateExecutiveMemo}
              onDownloadReport={reportGeneration.handleDownloadDiscoveryReport}
              onEmailReport={reportGeneration.handleEmailDiscoveryReport}
              reportGateNotice={reportGeneration.gateNotice}
              conciergeComplete={futureConcierge.conciergeComplete}
              returningVisitor={futureConcierge.returningVisitor}
              livingModeOverride={futureConcierge.livingModeOverride}
              {...(futureConcierge.conciergeSession
                ? { conciergeSession: futureConcierge.conciergeSession }
                : {})}
              onSubmitConcierge={futureConcierge.submitConcierge}
              onConfirmBriefing={futureConcierge.confirmBriefing}
              onCorrectBriefing={futureConcierge.correctBriefing}
              onResubmitCorrection={futureConcierge.resubmitCorrection}
            />
          </StageProvider>
        </Suspense>
      </ErrorBoundary>
    )
  }

  if (currentView === 'admin')
    return (
      <ErrorBoundary>
        <Suspense
          fallback={
            <div className="flex items-center justify-center h-screen text-foreground">
              Loading admin…
            </div>
          }
        >
          <AdminV2Dashboard onClose={goToLanding} />
        </Suspense>
      </ErrorBoundary>
    )
  if (currentView === 'chat' || currentView === 'future-chat') {
    return renderFutureChatShell()
  }
  if (currentView === 'about')
    return (
      <AboutPage
        onBack={goToLanding}
        onStartChat={() => leadResearch.handleStartChatRequest(false)}
      />
    )
  if (currentView === 'ai-agency')
    return (
      <AiAgencyPage
        onBack={goToLanding}
        onStartChat={() => leadResearch.handleStartChatRequest(false)}
      />
    )
  if (currentView === 'fixer') return <FixerPage onBack={goToLanding} />
  if (currentView === 'workshops') return <WorkshopsPage />
  if (currentView === 'not-found') return <NotFoundPage />
  if (currentView === 'privacy') return <PrivacyPolicy />
  if (currentView === 'terms') return <TermsOfService />

  const landingCanvasStyle =
    currentView === 'landing'
      ? {
          WebkitMaskImage:
            'radial-gradient(ellipse 92% 82% at 52% 48%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 50%, rgba(0,0,0,0.45) 78%, rgba(0,0,0,0.08) 94%, rgba(0,0,0,0) 100%)',
          maskImage:
            'radial-gradient(ellipse 92% 82% at 52% 48%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 50%, rgba(0,0,0,0.45) 78%, rgba(0,0,0,0.08) 94%, rgba(0,0,0,0) 100%)',
        }
      : undefined

  return (
    <div
      className={`min-h-[100dvh] flex flex-col transition-colors duration-500 ${isDarkMode ? 'dark' : ''} ${SURFACE_ROLES.base}`}
    >
      <a
        href={`#${mainContentId}`}
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:text-sm focus:font-medium"
      >
        Skip to main content
      </a>
      <BrowserCompatibility />
      {/* Background Canvas: restore the original Antigravity particle system across landing and chat */}
      <div
        className="absolute inset-0 z-0 pointer-events-none overflow-hidden"
        style={landingCanvasStyle}
      >
        <AntigravityCanvas
          visualState={visualState}
          surface={currentView === 'landing' ? 'landing' : 'page'}
          userProfile={leadResearch.userProfile}
          onShapeChange={handleCanvasShapeChange}
          isDarkMode={isDarkMode}
          getAudioData={getAudioData}
        />
      </div>

      <AppHeader
        currentView={currentView}
        goToLanding={goToLanding}
        isDarkMode={isDarkMode}
        activeRoute={activeRoute}
        connectionState={connectionState}
        backendStatus={backendStatus}
        isChatVisible={voiceChat.isChatVisible}
        openChat={voiceChat.openChat}
        isWebcamActive={voiceChat.isWebcamActive}
        isScreenShareActive={screenShare.isActive}
        isLocationShared={!!locationData}
        locationData={locationData}
      />
      <main id={mainContentId} className="relative z-[1]">
        {currentView === 'landing' && (
          <LandingPage
            onStartChat={() => leadResearch.handleStartChatRequest(false)}
            onSectionChange={shape => setVisualState(prev => ({ ...prev, shape }))}
            onAdminAccess={goToAdmin}
            onAboutAccess={() => setCurrentView('about')}
          />
        )}
      </main>
    </div>
  )
}

export default App
